/* eslint-disable react/no-danger */
import {useTheme} from '@mui/material/styles'
import PropTypes from 'prop-types'
import React, {useEffect, useMemo, useRef, useState} from 'react'

import {addExternalScript} from 'helpers/utils'
import {
  KEYRUS_AI_HUBSPOT_FORM_ID,
  KEYRUS_AI_HUBSPOT_PORTAL_ID,
  KEYRUS_AI_HUBSPOT_REGION,
} from 'helpers/keyrusAi'
import useIsMobile from 'hooks/useIsMobile'
import useParallaxEnabled from 'hooks/useParallaxEnabled'

import logo from '../KeyrusAiHero/assets/logo.svg'

const SECTION_TITLE = 'Keyrus, Architect of intelligence'
const SECTION_SUBTITLE = 'We operationalize intelligence.'
const SUBMIT_LABEL = 'Contact our architects'
const LOGO_ALT = 'keyrus Ai — Architect of intelligence'

const PRIVACY_POLICY_URL = 'https://keyrus.com/worldwide/privacy-policy'
const TERMS_URL = 'https://keyrus.com/worldwide/legal-notice-and-terms-of-use'

const HUBSPOT_SCRIPT = 'https://js-eu1.hsforms.net/forms/v2.js'
const HUBSPOT_TARGET_ID = 'keyrusAiHubspotForm'

const NAV_OFFSET = 90

const getRevealStyle = (isRevealed, delay) => ({
  opacity: isRevealed ? 1 : 0,
  transform: isRevealed ? 'translateY(0)' : 'translateY(48px)',
  transition: `opacity 0.8s ease ${delay}s, transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ${delay}s`,
})

const useReveal = () => {
  const ref = useRef(null)
  const [isRevealed, setIsRevealed] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const prefersReducedMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      setIsRevealed(true)

      return undefined
    }

    const node = ref.current

    if (!node) {
      return undefined
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsRevealed(true)
            observer.disconnect()
          }
        })
      },
      {rootMargin: '0px 0px -12% 0px', threshold: 0.12},
    )

    observer.observe(node)

    return () => observer.disconnect()
  }, [])

  return [ref, isRevealed]
}

const getStyles = (theme, isMobile) => {
  const palette = theme.palette || {}
  const background = palette.background || {}
  const text = palette.text || {}
  const deepNavy = background.deepNavy || '#171531'
  const white = text.light || '#ffffff'
  const cyan = palette.primary?.main || '#28c9ff'

  return {
    root: {
      position: 'relative',
      width: '100%',
      overflow: 'hidden',
      background: `
        radial-gradient(46% 40% at 16% 100%, rgba(243, 150, 32, 0.42) 0%, rgba(223, 10, 33, 0.20) 44%, rgba(23, 21, 49, 0) 78%),
        radial-gradient(42% 42% at 84% 100%, rgba(6, 189, 255, 0.40) 0%, rgba(0, 49, 137, 0.22) 46%, rgba(23, 21, 49, 0) 80%),
        radial-gradient(36% 30% at 50% 100%, rgba(218, 1, 39, 0.22) 0%, rgba(23, 21, 49, 0) 70%),
        linear-gradient(180deg, #100f25 0%, ${deepNavy} 58%)
      `,
      color: white,
      padding: isMobile ? '72px 16px 64px' : '120px 24px 88px',
      fontFamily: "'Montserrat', sans-serif",
      scrollMarginTop: isMobile ? 20 : 90,
    },
    cover: {
      zIndex: 2,
      marginTop: `calc(-100vh + ${NAV_OFFSET}px)`,
      borderRadius: '40px 40px 0 0',
    },
    inner: {
      position: 'relative',
      maxWidth: 1280,
      margin: '0 auto',
      zIndex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    header: {
      textAlign: 'center',
      marginBottom: isMobile ? 36 : 48,
    },
    title: {
      margin: 0,
      color: white,
      fontSize: isMobile ? 25 : 'clamp(30px, 4.4vw, 52px)',
      fontWeight: 800,
      lineHeight: isMobile ? 1.24 : 1.12,
    },
    subtitle: {
      margin: '12px 0 0',
      color: 'rgba(255, 255, 255, 0.78)',
      fontSize: isMobile ? 16 : 20,
      fontWeight: isMobile ? 500 : 400,
      lineHeight: 1.4,
    },
    card: {
      width: '100%',
      maxWidth: isMobile ? 360 : 550,
      boxSizing: 'border-box',
      background: white,
      borderRadius: isMobile ? 12 : 20,
      padding: isMobile ? '28px 22px' : '41px 47px',
      boxShadow: '0 30px 70px rgba(0, 0, 0, 0.35)',
    },
    hubspotForm: {
      width: '100%',
    },
    policies: {
      marginTop: 18,
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    },
    policyText: {
      margin: 0,
      color: '#5b5b66',
      fontSize: 12,
      lineHeight: 1.45,
    },
    policyLink: {
      color: cyan,
      textDecoration: 'underline',
    },
    logoSlot: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: isMobile ? 48 : 72,
    },
    logo: {
      width: isMobile ? 'min(220px, 64vw)' : 260,
      height: 'auto',
      filter: 'brightness(0) invert(1)',
    },
  }
}

const getFormCss = (theme, isMobile) => {
  const cyan = theme.palette?.primary?.main || '#28c9ff'
  const columns = isMobile ? '1fr' : '1fr 1fr'
  const columnsGap = isMobile ? 12 : 21
  const target = `#${HUBSPOT_TARGET_ID}`

  return `
    ${target} .hs-form { display: flex; flex-direction: column; gap: 12px; }
    ${target} fieldset { max-width: none !important; width: 100% !important; }
    ${target} .form-columns-2 { display: grid; grid-template-columns: ${columns}; gap: ${columnsGap}px; }
    ${target} .form-columns-2 .hs-form-field { width: auto !important; float: none !important; padding: 0 !important; }
    ${target} .form-columns-1 .hs-form-field { width: auto !important; float: none !important; }
    ${target} .form-columns-1 .input { margin-right: 0 !important; }
    ${target} .hs-form-field { box-sizing: border-box; }
    ${target} .hs-form-field > label { display: block; color: #808080; font-family: 'Poppins', sans-serif; font-size: ${
    isMobile ? 12 : 16
  }px; line-height: 1.2; margin-bottom: 5px; }
    ${target} .hs-form-field > label .hs-form-required { color: #da0127; margin-left: 2px; }
    ${target} .input { margin: 0 !important; }
    ${target} .hs-input:not([type="checkbox"]):not([type="radio"]) { box-sizing: border-box; width: 100% !important; height: ${
    isMobile ? 32 : 46
  }px; padding: 0 14px; border-radius: ${
    isMobile ? 5 : 8
  }px; border: 1px solid #e3e3e3; background: #f6f6f6; color: #303030; font-family: 'Montserrat', sans-serif; font-size: 15px; outline: none; }
    ${target} textarea.hs-input { height: auto; min-height: 101px; padding: 12px 14px; line-height: 1.45; resize: vertical; }
    ${target} .hs-error-msgs { list-style: none; margin: 6px 0 0; padding: 0; }
    ${target} .hs-error-msg, ${target} .hs-error-msgs label { color: #da0127; font-size: 13px; line-height: 1.3; }
    ${target} .legal-consent-container { margin-top: 4px; }
    ${target} .legal-consent-container, ${target} .legal-consent-container * { font-size: 12px !important; line-height: 1.4; color: #5b5b66; }
    ${target} .hs-richtext { color: #5b5b66; font-size: 13px; line-height: 1.4; }
    ${target} [class*="captcha"], ${target} iframe[src*="recaptcha"], ${target} iframe[title*="recaptcha"] { display: none !important; }
    ${target} .hs_recaptcha { display: none !important; }
    ${target} .hs-form-field:has(input[type="hidden"]) { display: none !important; }
    ${target} fieldset:has(input[type="hidden"]):not(:has(input[type="text"], input[type="email"], input[type="tel"], input[type="number"], input[type="password"], textarea, select)) { display: none !important; }
    ${target} fieldset[class*="recaptcha"], ${target} fieldset.hs-fieldtype-recaptcha { display: none !important; }
    ${target} .hs_submit { margin-top: 0; }
    ${target} .hs-button { display: block; width: 100%; height: ${
    isMobile ? 45 : 56
  }px; border: none; border-radius: ${
    isMobile ? 7 : 11
  }px; background: ${cyan}; color: #fff; font-family: 'Montserrat', sans-serif; font-size: ${
    isMobile ? 16 : 20
  }px; font-weight: 600; cursor: pointer; transition: filter 0.2s ease; }
    ${target} .hs-button:hover { filter: brightness(0.97); }
    ${target} .submitted-message { color: #0f7b3f; font-size: 16px; line-height: 1.5; text-align: center; padding: 8px 0; margin: 0; }
  `
}

const KeyrusAiContact = ({copy, salesforceCountry}) => {
  const theme = useTheme()
  const isMobile = useIsMobile()
  const parallaxEnabled = useParallaxEnabled()
  const styles = useMemo(() => getStyles(theme, isMobile), [theme, isMobile])
  const formCss = useMemo(() => getFormCss(theme, isMobile), [theme, isMobile])

  const title = copy.title || SECTION_TITLE
  const subtitle = copy.subtitle || SECTION_SUBTITLE
  const submitLabel = copy.submitLabel || SUBMIT_LABEL
  const hubspotLocale = copy.hubspotLocale || 'en'
  const {fields: fieldLabels, policy} = copy

  const [sectionRef, isRevealed] = useReveal()
  const [formReady, setFormReady] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const cancelledRef = {current: false}

    const createForm = () => {
      if (cancelledRef.current || !window.hbspt?.forms) {
        return
      }

      window.hbspt.forms.create({
        region: KEYRUS_AI_HUBSPOT_REGION,
        portalId: KEYRUS_AI_HUBSPOT_PORTAL_ID,
        formId: KEYRUS_AI_HUBSPOT_FORM_ID,
        target: `#${HUBSPOT_TARGET_ID}`,
        locale: hubspotLocale,
        translations: {
          [hubspotLocale]: {
            submitText: submitLabel,
          },
        },
      })
    }

    if (window.hbspt?.forms) {
      createForm()
    } else {
      addExternalScript(HUBSPOT_SCRIPT, createForm)
    }

    return () => {
      cancelledRef.current = true
    }
  }, [hubspotLocale, submitLabel])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const handleMessage = event => {
      if (event.data?.type !== 'hsFormCallback') {
        return
      }

      if (event.data.eventName === 'onFormReady') {
        setFormReady(true)

        const lastLandingPage = document.querySelector(
          `#${HUBSPOT_TARGET_ID} input[name="last_landing_page"]`,
        )

        if (lastLandingPage) {
          lastLandingPage.value = window.location.href
        }

        // Populate the hidden HubSpot "Lead Country" field (leadcountry__c) so
        // leads from the gateway / /keyrus-ai carry a country, mirroring every
        // other HubSpot form (see UI/HubspotContactForm onFormReady).
        const leadCountry = document.querySelector(
          `#${HUBSPOT_TARGET_ID} input[name="leadcountry__c"]`,
        )

        if (leadCountry && salesforceCountry) {
          leadCountry.value = salesforceCountry
        }

        const submitButton = document.querySelector(
          `#${HUBSPOT_TARGET_ID} input[type="submit"]`,
        )

        if (submitButton) {
          submitButton.value = submitLabel
        }

        // HubSpot serves this form in English. Rewrite ONLY the visible label
        // text for the current locale, keyed by the input's `name`. HubSpot
        // wraps the label text in a <span> alongside a `.hs-form-required`
        // asterisk span, so we target the text span (not the asterisk) and
        // replace its text — never insert, to avoid "NombreFirst Name" doubling.
        // Field names, inputs and submission behaviour are left untouched.
        // Relabel one control's visible <label> text (and placeholder) for the
        // current locale. HubSpot wraps the label text in a <span> next to the
        // `.hs-form-required` asterisk span, so we target the text span (not the
        // asterisk) and replace its text — never insert, to avoid doubling.
        const relabelInput = (input, label) => {
          const field = input && input.closest('.hs-form-field')
          const labelEl = field && field.querySelector(':scope > label')

          if (!labelEl) {
            return
          }

          const textSpan = labelEl.querySelector('span:not(.hs-form-required)')

          if (textSpan) {
            textSpan.textContent = label
          } else {
            // No wrapper span: replace the label's own leading text node,
            // keeping the trailing required-asterisk span intact.
            const textNode = Array.from(labelEl.childNodes).find(
              node => node.nodeType === 3 && node.textContent.trim(),
            )

            if (textNode) {
              textNode.textContent = label
            }
          }

          if (input && 'placeholder' in input && input.placeholder) {
            input.placeholder = label
          }
        }

        if (fieldLabels) {
          Object.entries(fieldLabels).forEach(([name, label]) => {
            relabelInput(
              document.querySelector(`#${HUBSPOT_TARGET_ID} [name="${name}"]`),
              label,
            )
          })

          // The message field's HubSpot internal name differs from "message"
          // (custom property), so the name-based pass above misses it. It is the
          // form's only textarea, so relabel that directly as a fallback.
          if (fieldLabels.message) {
            relabelInput(
              document.querySelector(`#${HUBSPOT_TARGET_ID} textarea`),
              fieldLabels.message,
            )
          }
        }
      }

      if (event.data.eventName === 'onFormSubmitted') {
        setSubmitted(true)
      }
    }

    window.addEventListener('message', handleMessage)

    return () => window.removeEventListener('message', handleMessage)
  }, [submitLabel, fieldLabels, salesforceCountry])

  return (
    <section
      aria-labelledby="keyrus-ai-contact-title"
      id="contact"
      ref={sectionRef}
      style={parallaxEnabled ? {...styles.root, ...styles.cover} : styles.root}
    >
      <style dangerouslySetInnerHTML={{__html: formCss}} />
      <div style={styles.inner}>
        <header style={{...styles.header, ...getRevealStyle(isRevealed, 0)}}>
          <h2 id="keyrus-ai-contact-title" style={styles.title}>
            {title}
          </h2>
          <p style={styles.subtitle}>{subtitle}</p>
        </header>

        <div style={{...styles.card, ...getRevealStyle(isRevealed, 0.15)}}>
          <div
            id={HUBSPOT_TARGET_ID}
            style={{...styles.hubspotForm, minHeight: formReady ? 0 : 300}}
          />

          {!submitted && (
            <div style={styles.policies}>
              <p style={styles.policyText}>
                {policy?.consentLead ||
                  'By submitting this form, you agree that Keyrus may process the information provided to respond to your request. To learn how we handle your personal data and your rights, please read our'}{' '}
                <a
                  href={PRIVACY_POLICY_URL}
                  rel="noreferrer"
                  style={styles.policyLink}
                  target="_blank"
                >
                  {policy?.privacyLabel || 'Privacy Policy'}
                </a>
                .
              </p>
              <p style={styles.policyText}>
                {policy?.moreLead || 'For more information, see our'}{' '}
                <a
                  href={TERMS_URL}
                  rel="noreferrer"
                  style={styles.policyLink}
                  target="_blank"
                >
                  {policy?.termsLabel || 'Legal Notice & Terms of Use'}
                </a>
                .
              </p>
            </div>
          )}
        </div>

        <div style={{...styles.logoSlot, ...getRevealStyle(isRevealed, 0.3)}}>
          <img alt={LOGO_ALT} src={logo} style={styles.logo} />
        </div>
      </div>
    </section>
  )
}

KeyrusAiContact.propTypes = {
  copy: PropTypes.shape({
    fields: PropTypes.objectOf(PropTypes.string),
    hubspotLocale: PropTypes.string,
    policy: PropTypes.shape({
      consentLead: PropTypes.string,
      moreLead: PropTypes.string,
      privacyLabel: PropTypes.string,
      termsLabel: PropTypes.string,
    }),
    submitLabel: PropTypes.string,
    subtitle: PropTypes.string,
    title: PropTypes.string,
  }),
  salesforceCountry: PropTypes.string,
}

KeyrusAiContact.defaultProps = {
  copy: {},
  salesforceCountry: '',
}

export default KeyrusAiContact
